/**
 * Cart Restrictions UI Handler
 * Manages UI updates for cart restrictions (quantity controls, price elements)
 */

class CartRestrictionsUI {
  constructor(restrictionsManager) {
    this.restrictionsManager = restrictionsManager;
    this.observer = null;
  }

  /**
   * Hides quantity controls for one-unit-only products in the cart
   * @param {Object} cart - Cart data object
   */
  hideQuantityControlsForOneUnitProducts(cart) {
    const oneUnitProductsInCart = this.restrictionsManager.getOneUnitProductsInCart(cart);
    
    if (!oneUnitProductsInCart.length) return;
    
    oneUnitProductsInCart.forEach(cartItem => {
      const productData = this.restrictionsManager.getOneUnitProductData(cartItem.product_id);
      if (productData?.handle) {
        this.hideQuantityControlsForProduct(productData.handle);
      }
    });
  }

  /**
   * Updates quantity selector visibility on product pages based on cart state
   * @param {Object} cart - Cart data object
   */
  updateQuantitySelectorVisibility(cart) {
    // Get all one-unit-only products
    const oneUnitProducts = this.restrictionsManager.oneUnitOnlyProducts || [];
    
    oneUnitProducts.forEach(product => {
      // Check if this product is in the cart
      const isInCart = this.restrictionsManager.isProductInCart(product.id, cart);
      
      // Find quantity selector for this product on the current page
      const quantitySelectors = document.querySelectorAll('.product__quantity');
      
      quantitySelectors.forEach(selector => {
        // Check if this selector belongs to the current product
        const productBlock = selector.closest('.product-block');
        if (productBlock) {
          // For now, we'll show the quantity selector if the product is NOT in cart
          // This is the opposite of the cart logic - we want to show quantity selector
          // on product pages when the product is not in cart
          if (!isInCart) {
            selector.style.display = 'block';
          } else {
            selector.style.display = 'none';
          }
        }
      });
    });
  }

  /**
   * Hides quantity controls for a specific product by handle
   * @param {string} productHandle - Product handle
   */
  hideQuantityControlsForProduct(productHandle) {
    const cartItemSelector = `.product-${productHandle}`;
    
    // Hide quantity controls
    const quantitySelector = `${cartItemSelector} .rebuy-cart__flyout-item-quantity`;
    document.querySelectorAll(quantitySelector).forEach(control => {
      control.style.display = 'none';
    });
    
    // Set price element width to 100%
    const priceSelector = `${cartItemSelector} .rebuy-cart__flyout-item-price`;
    document.querySelectorAll(priceSelector).forEach(priceElement => {
      priceElement.style.width = '100%';
    });
  }

  /**
   * Sets up a MutationObserver to watch for new quantity selectors and hide them for one-unit-only products
   */
  setupQuantitySelectorObserver() {
    // Create observer to watch for new quantity selectors
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        // Check for added nodes
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            this.processAddedNode(node);
          }
        });
      });
    });

    // Start observing the document body for changes
    this.observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return this.observer;
  }

  /**
   * Processes a newly added DOM node to check for quantity selectors
   * @param {Element} node - The added DOM node
   */
  processAddedNode(node) {
    // Check if the added node is a quantity selector
    const quantitySelectors = node.querySelectorAll ? 
      node.querySelectorAll('.rebuy-cart__flyout-item-quantity') : 
      (node.classList && node.classList.contains('rebuy-cart__flyout-item-quantity') ? [node] : []);
    
    quantitySelectors.forEach(selector => {
      this.processQuantitySelector(selector);
    });
  }

  /**
   * Processes a quantity selector element to check if it should be hidden
   * @param {Element} selector - The quantity selector element
   */
  processQuantitySelector(selector) {
    // Find the parent cart item to get the product handle
    const cartItem = selector.closest('.rebuy-cart__flyout-item');
    if (!cartItem) return;

    // Extract product handle from cart item classes
    const productHandle = Array.from(cartItem.classList)
      .find(className => className.startsWith('product-'))
      ?.replace('product-', '');
    
    if (!productHandle) return;

    // Check if this product is one-unit-only
    const isOneUnitProduct = this.restrictionsManager.oneUnitOnlyProducts.some(product => 
      product.handle === productHandle
    );
    
    if (isOneUnitProduct) {
      // Hide the quantity selector
      selector.style.display = 'none';
      
      // Set price element width to 100%
      const priceElement = cartItem.querySelector('.rebuy-cart__flyout-item-price');
      if (priceElement) {
        priceElement.style.width = '100%';
      }
    }
  }

  /**
   * Disconnects the MutationObserver
   */
  disconnect() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

// Export the UI handler class
window.CartRestrictionsUI = CartRestrictionsUI; 